import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Phone, Mail, Menu, X, Globe } from "lucide-react";
import { SunMark } from "./SunMark";
import { T, paths, otherLangPath, PHONE_HREF, EMAIL_HREF } from "../i18n";
import { SERVICES } from "../data/services";
import { CITIES } from "../data/cities";

const TOP_CITY_SLUGS = ["montreal", "quebec-city", "laval", "gatineau", "longueuil", "sherbrooke", "saguenay", "levis", "trois-rivieres", "terrebonne", "drummondville", "rimouski"];

const RESOURCES = [
  ["Bureau de la sécurité privée (BSP)", "https://www.bspquebec.ca"],
  ["Sûreté du Québec", "https://www.sq.gouv.qc.ca"],
  ["CNESST — Santé et sécurité du travail", "https://www.cnesst.gouv.qc.ca"],
  ["Ville de Montréal", "https://montreal.ca"],
  ["Ville de Québec", "https://www.ville.quebec.qc.ca"]
];

export default function Layout({ lang }) {
  const t = T[lang];
  const p = paths(lang);
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const altPath = otherLangPath(location.pathname);
  const topCities = TOP_CITY_SLUGS.map((s) => CITIES.find((c) => c.slug === s)).filter(Boolean);

  return (
    <div className="grain min-h-screen bg-obsidian text-slate-100">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-800/60 bg-obsidian/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to={p.home} className="flex items-center gap-3" data-testid="header-logo-link">
            <SunMark size={36} />
            <span className="flex flex-col leading-none">
              <span className="font-display text-lg font-extrabold tracking-tight">
                1st Nations <span className="text-amber-400">Security</span>
              </span>
              <span className="mt-1 hidden font-mono text-[9px] uppercase tracking-[0.3em] text-amber-400/70 sm:block">Quebec Security Services</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-6 lg:flex" data-testid="main-nav">
            <Link to={p.services} className="text-sm font-medium text-slate-300 transition-colors hover:text-amber-400" data-testid="nav-services">{t.nav.services}</Link>
            <Link to={p.cities} className="text-sm font-medium text-slate-300 transition-colors hover:text-amber-400" data-testid="nav-locations">{t.nav.locations}</Link>
            <Link to={p.guides} className="text-sm font-medium text-slate-300 transition-colors hover:text-amber-400" data-testid="nav-guides">{t.nav.guides}</Link>
            <Link to={p.careers} className="text-sm font-medium text-slate-300 transition-colors hover:text-amber-400" data-testid="nav-careers">{t.nav.careers}</Link>
            <Link to={p.contact} className="text-sm font-medium text-slate-300 transition-colors hover:text-amber-400" data-testid="nav-contact">{t.nav.contact}</Link>
            <Link to={altPath} className="flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-300 transition-colors hover:border-amber-500/50 hover:text-amber-400" data-testid="lang-toggle">
              <Globe size={13} /> {t.otherLang}
            </Link>
            <a href={PHONE_HREF} className="flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-bold text-obsidian transition-colors hover:bg-amber-300" data-testid="header-call-button">
              <Phone size={14} /> {t.stickyCall}
            </a>
          </nav>
          <button className="text-slate-200 lg:hidden" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu" data-testid="mobile-menu-button">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {menuOpen && (
          <nav className="border-t border-slate-800/60 bg-obsidian px-4 py-4 lg:hidden" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <Link to={p.services} className="text-base font-medium text-slate-200" data-testid="mobile-nav-services">{t.nav.services}</Link>
              <Link to={p.cities} className="text-base font-medium text-slate-200" data-testid="mobile-nav-locations">{t.nav.locations}</Link>
              <Link to={p.guides} className="text-base font-medium text-slate-200" data-testid="mobile-nav-guides">{t.nav.guides}</Link>
              <Link to={p.careers} className="text-base font-medium text-slate-200" data-testid="mobile-nav-careers">{t.nav.careers}</Link>
              <Link to={p.contact} className="text-base font-medium text-slate-200" data-testid="mobile-nav-contact">{t.nav.contact}</Link>
              <Link to={altPath} className="flex items-center gap-1.5 text-sm font-semibold text-amber-400" data-testid="mobile-lang-toggle"><Globe size={14} /> {t.otherLang}</Link>
            </div>
          </nav>
        )}
      </header>

      <main className="pt-16 pb-24">
        <Outlet />
      </main>

      <footer className="border-t border-slate-800/60 bg-card/50">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 md:grid-cols-2 lg:grid-cols-4 lg:px-8">
          <div>
            <div className="flex items-center gap-3">
              <SunMark size={34} animate={false} />
              <span className="flex flex-col leading-none">
                <span className="font-display text-lg font-extrabold">1st Nations <span className="text-amber-400">Security</span></span>
                <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-amber-400/70">Quebec Security Services</span>
              </span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">{t.footerTag}</p>
            <p className="mt-4 font-mono text-xs text-amber-400/80">{t.heroBadge1} · {t.heroBadge2}</p>
          </div>
          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">{t.footerServices}</h3>
            <ul className="mt-4 space-y-2">
              {SERVICES.slice(0, 8).map((s) => (
                <li key={s.slug}>
                  <Link to={p.service(s.slug)} className="text-sm text-slate-400 transition-colors hover:text-amber-300" data-testid={`footer-service-${s.slug}`}>
                    {s[lang].name}
                  </Link>
                </li>
              ))}
              <li><Link to={p.services} className="text-sm font-semibold text-amber-400" data-testid="footer-all-services">{t.allServicesCta} →</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">{t.footerCities}</h3>
            <ul className="mt-4 space-y-2">
              {topCities.map((c) => (
                <li key={c.slug}>
                  <Link to={p.city(c.slug)} className="text-sm text-slate-400 transition-colors hover:text-amber-300" data-testid={`footer-city-${c.slug}`}>
                    {lang === "fr" ? c.fr : c.en}
                  </Link>
                </li>
              ))}
              <li><Link to={p.cities} className="text-sm font-semibold text-amber-400" data-testid="footer-all-cities">{t.allCitiesCta} →</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">{t.footerCompany}</h3>
            <ul className="mt-4 space-y-2">
              <li><Link to={p.guides} className="text-sm text-slate-400 transition-colors hover:text-amber-300" data-testid="footer-guides">{t.nav.guides}</Link></li>
              <li><Link to={p.careers} className="text-sm text-slate-400 transition-colors hover:text-amber-300" data-testid="footer-careers">{t.nav.careers}</Link></li>
              <li><Link to={p.contact} className="text-sm text-slate-400 transition-colors hover:text-amber-300" data-testid="footer-contact">{t.nav.contact}</Link></li>
              <li><a href="https://1stnation-security.com" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition-colors hover:text-amber-300" data-testid="sister-site-link">1stnation-security.com</a></li>
            </ul>
            <h3 className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-amber-400">{t.footerResources}</h3>
            <ul className="mt-4 space-y-2">
              {RESOURCES.map(([name, url]) => (
                <li key={url}>
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 transition-colors hover:text-amber-300" data-testid={`footer-resource-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800/60 py-6 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} 1st Nations Security — Quebec Security Services. {t.rights}
        </div>
      </footer>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-amber-500/30 bg-[#131722]/95 backdrop-blur-md" data-testid="sticky-contact-bar">
        <div className="mx-auto flex max-w-7xl items-center justify-center gap-3 p-3 sm:justify-between sm:px-6 lg:px-8">
          <span className="hidden font-mono text-xs uppercase tracking-[0.25em] text-amber-400/80 sm:block">{t.dispatchLabel}</span>
          <div className="flex items-center gap-3">
            <a href={PHONE_HREF} className="flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-bold text-obsidian transition-all hover:bg-amber-300 hover:shadow-[0_0_24px_rgba(245,124,0,0.4)]" data-testid="sticky-call-button">
              <Phone size={15} /> {t.stickyCall}
            </a>
            <a href={EMAIL_HREF} className="flex items-center gap-2 rounded-full border border-amber-500/50 px-5 py-2.5 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/10" data-testid="sticky-email-button">
              <Mail size={15} /> <span className="hidden sm:inline">{t.stickyEmail}</span><span className="sm:hidden">{t.emailUs}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
